import DataLayerHelper from '@helpers/data-layer.helper'
import User from '@interfaces/user.interface'
import onUserChange from '@observables/user.observable'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import { ISocialNetwork } from '../../modal-vinculate-social-network'
import ImportanceComponent from '../importance/importance.component'
import Selector from '@components/inputs/selector/selector.component'
import ColorIconHelper from '@/helpers/colorIcon.helper'

import './select-network-content.component.scoped.scss'

interface Props {
  socialNetwork: string
  width: number
  height: number
  availableSocialNetworks: ISocialNetwork[]
  select: boolean
  setSocialNetwork: (socialNetwork: string) => void
  hasSelected: () => void
  reset: () => void
}

export default ({ socialNetwork, width, height, availableSocialNetworks, select, hasSelected, reset, setSocialNetwork }: Props) => {
  const colorIconHelper = new ColorIconHelper()
  const dataLayerHelper = useMemo(() => new DataLayerHelper(), [])
  const [user, setUser] = useState<User | null>(null)
  const [selectedType, setSelectedType] = useState<string>('')

  const socialNetworkInfo = useMemo(
    () => ({
      facebook: {
        name: 'Instagram',
        icon: 'instagram',
      },
      tiktok: {
        name: 'TikTok',
        icon: 'tiktok',
      },
      youtube: {
        name: 'Youtube',
        icon: 'youtube',
      },
      twitter: {
        name: 'Twitter - X',
        icon: 'x-twitter',
      },
    }),
    []
  )

  const { t } = useTranslation('modalVinculateSocialNetwork')

  const handleSelectSocialNetwork = useCallback(
    async ({ type }: { type: string }) => {
      setSocialNetwork(type)
      await dataLayerHelper?.sendToDataLayer({
        event: 'social_network_session',
        step: 'Escolha uma rede social',
        socialNetwork: type === 'facebook' ? 'instagram' : type,
        action: 'next',
        description: 'coleta de click para conectar redes',
      })
    },
    [dataLayerHelper, setSocialNetwork]
  )

  useEffect(() => {
    if (select) {
      handleSelectSocialNetwork({ type: selectedType })
      reset()
    }
  }, [select, handleSelectSocialNetwork, reset, selectedType])

  useEffect(() => {
    // Set user attributes for targeting (from cookie, auth system, etc.)
    const onUserChangeSubscription: SubscriptionLike = onUserChange?.subscribe((user: User) => {
      if (user) {
        setUser(user)
      }
    })

    return () => onUserChangeSubscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!socialNetwork) {
    return (
      <div
        className="wrapper-select-network"
        style={{
          height: width < 991 ? `calc(${height}px - 125px - 1rem)` : null,
        }}
      >
        <h5 className="title">{t('connectYourSocialNetwork')}</h5>

        {availableSocialNetworks?.map(({ title, type }: ISocialNetwork) => {
          return (
            <Selector
              name={`social-network-add-social-network-new-${type}`}
              id={`social-network-add-social-network-new-${type}`}
              errorSpan={false}
              value={type}
              checked={type === selectedType}
              onChange={(checked: boolean) => {
                if (checked) {
                  setSelectedType(type)
                  hasSelected()
                }
              }}
              rightLabel={
                <div
                  className={'display-grid justify-content-center p-3 social-network-item'}
                  style={{
                    border: '1px solid var(--border_color)',
                    borderRadius: '8px',
                    color: type === selectedType ? 'var(--color_bg_button_brands_wake)' : 'inherit',
                    borderColor: type === selectedType ? 'var(--color_bg_button_brands_wake)' : 'inherit',
                    background: type === selectedType ? 'var(--color_bg_box_brands_squid)' : 'inherit',
                    width: '400px',
                  }}
                >
                  <i
                    className={`display-flex justify-content-center fa-brands fa-${colorIconHelper.setIcon(
                      socialNetworkInfo[type].icon
                    )} mb-2`}
                    style={{ fontSize: '22px', color: 'inherit' }}
                  ></i>
                  <span className="text">{socialNetworkInfo[type].name}</span>
                </div>
              }
              hideInput={true}
              type="radio"
              labelStyle={{ width: '100%' }}
            />
          )
        })}
      </div>
    )
  } else {
    if (socialNetwork === 'facebook') {
      return <ImportanceComponent />
    } else {
      const listItems: ReactElement[] = [
        <span key={'warning-metrics-0'} dangerouslySetInnerHTML={{ __html: t('warningMetrics.0') }} />,
        <span key={'warning-metrics-1'} dangerouslySetInnerHTML={{ __html: t('warningMetrics.1') }} />,
        <span key={'warning-metrics-2'} dangerouslySetInnerHTML={{ __html: t('warningMetrics.2') }} />,
      ]

      return (
        <div className="wrapper-importance" style={{ height: width < 991 ? `calc(${height}px - 204px)` : 'auto' }}>
          <h4 className="title text-center">{t('questionVinculate')}</h4>
          <div className="content-list-importance">
            {listItems?.map((description, index) => (
              <div className="item-list-importance" key={index}>
                <div className="item-list-importance-icon">
                  <i className="fa-regular fa-circle-check" style={{ color: 'var(--green)' }}></i>
                </div>
                <div className="description">{description}</div>
              </div>
            ))}
          </div>
          {['facebook', 'instagram'].includes(socialNetwork) && (
            <span className="orientation-tutorial text-center" dangerouslySetInnerHTML={{ __html: t('instructions') }} />
          )}
        </div>
      )
    }
  }
}
